import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { useRouter } from "next/router";
import { withActiveToken } from "../lib/auth";
import { getAPIURL } from "../lib/utils";
import BusinessContext from "./business-context";
import { CREATED_AT_COLUMN, CREATED_AT_FILTER_ID } from "../components/ui/created-at-filter-type";

const TableFilterContext = createContext({
activeFilters: null,
setActiveFilters: () => {},
filterValues: null,
setFilterValues: () => {},
newFilterId: null,
setNewFilterId: () => {},
initializeFiltersFromColumns: () => {},
});

export const TableFilterProvider = (props) => {
    const router = useRouter();
    const [activeFilters, setActiveFilters] = useState(null);
    const [filterValues, setFilterValues] = useState(null);
    const [newFilterId, setNewFilterId] = useState(null);

  // Initialize filterValues from URL query params once router is ready (no filters case)
  useEffect(() => {
    if (!router.isReady) return;
    const hasColFilters = Object.keys(router.query).some(k => k.startsWith('col_'));
    if (!hasColFilters) {
      setFilterValues(prev => prev !== null ? prev : {});
      setActiveFilters(prev => prev !== null ? prev : []);
    }
    // If there ARE col_ filters, initialization is deferred to initializeFiltersFromColumns
    // which is called once tableData (with columns) is available.
  }, [router.isReady, router.query]);

  // Called by [id].js once tableData is loaded, to parse URL filters with proper column type info
  const initializeFiltersFromColumns = useCallback((columns) => {
    if (!router.isReady || !columns) return;
    if (activeFilters !== null) return; // already initialized

    const query = router.query;
    const newFilterValues = {};
    const newActiveFilters = [];

    Object.entries(query).forEach(([key, value]) => {
      if (!key.startsWith('col_')) return;
      const colId = key.replace('col_', '');
      const columnInfo = colId === CREATED_AT_FILTER_ID
        ? CREATED_AT_COLUMN
        : columns.find(col => col.id === colId);
      if (!columnInfo) return;
      if (
        columnInfo.type === 'mutable_select' ||
        columnInfo.type === 'select' ||
        columnInfo.type === 'many_to_many'
      ) {
        newFilterValues[colId] = value.split(',').map(v => decodeURIComponent(v));
      } else if (columnInfo.type === 'teammate') {
        newFilterValues[colId] = value.split(',').map(v => Number(v));
      } else if (columnInfo.type === 'number') {
        newFilterValues[colId] = Number(value);
      } else if (columnInfo.type === CREATED_AT_FILTER_ID) {
        // Kept as the raw `YYYY-MM-DD` / `YYYY-MM-DD..YYYY-MM-DD` string.
        newFilterValues[colId] = decodeURIComponent(value);
      } else {
        newFilterValues[colId] = decodeURIComponent(value);
      }
      newActiveFilters.push(columnInfo);
    });

    setFilterValues(newFilterValues);
    setActiveFilters(newActiveFilters);
  }, [router.isReady, router.query, activeFilters]);

  // Reset filters when navigating to a different page (pathname changes)
  useEffect(() => {
    const handleRouteChange = (url) => {
      const currentPath = router.asPath.split('?')[0];
      const newPath = url.split('?')[0];
      
      // Only reset if the pathname actually changed (not just query params)
      if (currentPath !== newPath) {
        setActiveFilters(null);
        setFilterValues(null);
        setNewFilterId(null);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  const context = {
    activeFilters,
    setActiveFilters,
    filterValues,
    setFilterValues,
    newFilterId,
    setNewFilterId,
    initializeFiltersFromColumns,
  };

  return (
    <TableFilterContext.Provider value={context}>
      {props.children}
    </TableFilterContext.Provider>
  );
};

export default TableFilterContext;
